import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Steps,
  Tag,
  Typography,
} from 'antd';
import {
  UserOutlined,
  AppstoreOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import {Status} from '../../graphql/API';
import {useEnrollmentForm} from './hooks/useEnrollmentForm';
import {StudentSelect} from './components/StudentSelect';
import {CurrencyPriceSection} from './components/CurrencyPriceSection';
import {EnrollmentSummary} from './components/EnrollmentSummary';

const {Title} = Typography;

const STEPS = [
  {title: 'Student', icon: <UserOutlined />},
  {title: 'Package', icon: <AppstoreOutlined />},
  {title: 'Schedule', icon: <CalendarOutlined />},
  {title: 'Payment', icon: <CreditCardOutlined />},
  {title: 'Review', icon: <CheckCircleOutlined />},
];

export const EnrollmentCreate = () => {
  const {
    form,
    students,
    packages,
    levels,
    grades,
    tests,
    curriculums,
    isLoadingStudents,
    isSubmitting,
    searchBy,
    setSearchBy,
    showCustomPrice,
    setShowCustomPrice,
    currentStep,
    setCurrentStep,
    onStudentSearch,
    onPopupScroll,
    onTestSearch,
    onSubmit,
    searchStudents,
    history,
    preSelectedStudent,
    preSelectedClass,
  } = useEnrollmentForm();

  const formValues = Form.useWatch([], form);

  return (
    <div style={{padding: '24px 32px', maxWidth: 960, margin: '0 auto'}}>
      <Title level={2} style={{textAlign: 'center', marginBottom: 32}}>
        Create Enrollment
      </Title>

      {/* Step Indicator */}
      <Steps
        current={currentStep}
        items={STEPS}
        style={{marginBottom: 32}}
        onChange={setCurrentStep}
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          placementStatus: 'TODO',
          paymentStatus: Status.TODO,
          paymentMethod: 'stripe',
          currency: 'EGP',
          useCustomPrice: false,
        }}
        onValuesChange={(_, all) => {
          // Auto-advance step indicator based on filled fields
          if (all.studentId && currentStep === 0) setCurrentStep(1);
          if (all.packageId && currentStep === 1) setCurrentStep(2);
          if (all.startDate && currentStep === 2) setCurrentStep(3);
          if (all.paymentStatus && currentStep === 3) setCurrentStep(4);
        }}>
        {/* ── Step 0: Student ── */}
        <Card
          title={
            <>
              <UserOutlined /> &nbsp; Student
            </>
          }
          style={{marginBottom: 16}}
          bodyStyle={{paddingBottom: 8}}>
          {preSelectedStudent || preSelectedClass ? (
            <>
              {preSelectedStudent && (
                <>
                  <Form.Item name="studentId" hidden>
                    <Input />
                  </Form.Item>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '8px 0',
                    }}>
                    <UserOutlined style={{fontSize: 20, color: '#1890ff'}} />
                    <div>
                      <Typography.Text strong style={{fontSize: 15}}>
                        {preSelectedStudent.name}
                      </Typography.Text>
                      <br />
                      <Typography.Text type="secondary" style={{fontSize: 12}}>
                        ID: {preSelectedStudent.id}
                      </Typography.Text>
                    </div>
                    <Tag color="blue" style={{marginLeft: 'auto'}}>
                      Pre-selected
                    </Tag>
                  </div>
                </>
              )}
              {preSelectedClass && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 0',
                  }}>
                  <AppstoreOutlined style={{fontSize: 20, color: '#1890ff'}} />
                  <div>
                    <Typography.Text strong style={{fontSize: 15}}>
                      {preSelectedClass.name}
                    </Typography.Text>
                    <br />
                    <Typography.Text type="secondary" style={{fontSize: 12}}>
                      Class ID: {preSelectedClass.id}
                    </Typography.Text>
                  </div>
                  <Tag color="blue" style={{marginLeft: 'auto'}}>
                    Class
                  </Tag>
                </div>
              )}
            </>
          ) : (
            <StudentSelect
              students={students}
              isLoading={isLoadingStudents}
              searchBy={searchBy}
              onSearchByChange={val => {
                setSearchBy(val);
                searchStudents(null);
              }}
              onSearch={onStudentSearch}
              onScroll={onPopupScroll}
              onChange={val => form.setFieldValue('studentId', val)}
            />
          )}
        </Card>

        {/* ── Step 1: Package & Level ── */}
        <Card
          title={
            <>
              <AppstoreOutlined /> &nbsp; Package & Level
            </>
          }
          style={{marginBottom: 16}}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="packageId"
                label="Package"
                rules={[{required: true, message: 'Please select a package'}]}>
                <Select
                  showSearch
                  placeholder="Select a package"
                  optionFilterProp="children">
                  {packages?.map(pkg => {
                    const id = (pkg as any)._id || pkg.id;
                    const name =
                      typeof pkg.name_i18n === 'string'
                        ? JSON.parse(pkg.name_i18n)['en']
                        : (pkg.name_i18n as any)?.en;
                    return (
                      <Select.Option value={id} key={id}>
                        {name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="curriculumId" label="Curriculum">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a curriculum (optional)"
                  optionFilterProp="children"
                  onChange={() => {
                    form.setFieldsValue({
                      levelId: undefined,
                      gradeId: undefined,
                    });
                  }}>
                  {curriculums?.map(curr => (
                    <Select.Option value={curr.id} key={curr.id}>
                      {curr.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="levelId" label="Level">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a level"
                  optionFilterProp="children"
                  onChange={() => form.setFieldsValue({gradeId: undefined})}>
                  {levels?.map(lvl => (
                    <Select.Option value={lvl.id!} key={lvl.id}>
                      {lvl.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="gradeId" label="Grade">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a grade"
                  optionFilterProp="children"
                  onChange={() => form.setFieldsValue({levelId: undefined})}>
                  {grades?.map(grd => (
                    <Select.Option value={grd.id!} key={grd.id}>
                      {grd.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="testId" label="Placement Test">
                <Select
                  showSearch
                  placeholder="Select a test (optional)"
                  onSearch={onTestSearch}
                  optionFilterProp="children">
                  {tests?.map(tst => {
                    const id = (tst as any)._id || tst.id;
                    return (
                      <Select.Option value={id} key={id}>
                        {tst.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="planId" label="Plan ID">
                <Input placeholder="Enter plan ID (optional)" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Step 2: Schedule & Details ── */}
        <Card
          title={
            <>
              <CalendarOutlined /> &nbsp; Schedule & Details
            </>
          }
          style={{marginBottom: 16}}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startDate"
                label="Start Date"
                rules={[
                  {required: true, message: 'Please select a start date'},
                ]}>
                <DatePicker style={{width: '100%'}} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="endDate" label="End Date">
                <DatePicker style={{width: '100%'}} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="dayShift" label="Day Shift">
                <Input type="number" placeholder="Enter day shift (optional)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="partnerId" label="Partner ID">
                <Input placeholder="Enter partner ID (optional)" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Step 3: Payment ── */}
        <Card
          title={
            <>
              <CreditCardOutlined /> &nbsp; Payment
            </>
          }
          style={{marginBottom: 16}}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="paymentStatus"
                label="Payment Status"
                rules={[
                  {required: true, message: 'Please select payment status'},
                ]}>
                <Select placeholder="Select status">
                  <Select.Option value={Status.TODO}>TODO</Select.Option>
                  <Select.Option value={Status.DOING}>DOING</Select.Option>
                  <Select.Option value={Status.DONE}>DONE</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="paymentMethod"
                label="Payment Method"
                rules={[
                  {required: true, message: 'Please select a payment method'},
                ]}>
                <Select>
                  <Select.Option value="stripe">Stripe</Select.Option>
                  <Select.Option value="cash">Cash</Select.Option>
                  <Select.Option value="bank_transfer">
                    Bank Transfer
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="discountCodeId" label="Discount Code ID">
                <Input placeholder="Enter discount code ID (optional)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="paymentReferenceId" label="Payment Reference ID">
                <Input placeholder="Enter payment reference (optional)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isFree"
                label="Free Enrollment"
                valuePropName="checked">
                <Select placeholder="Is Free?">
                  <Select.Option value={false}>No</Select.Option>
                  <Select.Option value={true}>Yes</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <CurrencyPriceSection
            showCustomPrice={showCustomPrice}
            onToggleCustomPrice={setShowCustomPrice}
          />
        </Card>

        {/* ── Step 4: Summary ── */}
        <Card style={{marginBottom: 24}}>
          <EnrollmentSummary
            formValues={formValues}
            students={students}
            packages={packages}
            levels={levels}
            tests={tests}
            curriculums={curriculums}
            showCustomPrice={showCustomPrice}
          />
        </Card>

        {/* Submit */}
        <div style={{textAlign: 'center'}}>
          <Space>
            <Button size="large" onClick={() => history.goBack()}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              loading={isSubmitting}
              onClick={onSubmit}
              style={{minWidth: 160}}>
              Create Enrollment
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
};
